import axios from 'axios';
import { sesUrl } from '@/config/app.config';

/**
 * GS-901 — the result console's data: legacy results (SES `/api/director/*`, the old page's calls) and engine v2's
 * (SES `/api/director/v2/settlement/**` → gs-settlement-service `/admin/v2/**`), joined into one model so an operator
 * results a match the same way whichever engine runs it.
 */
export type Engine = 'legacy' | 'v2';
/** The product, as both engines number it: 1 Match Odds, 2 bookmaker, 3 fancy, 4 premium (engine v2 only, GS-894). */
export type Product = 1 | 2 | 3 | 4;
export type View = 'pending' | 'answered';

export interface Selection {
  id: number;
  name: string;
}

/**
 * GS-894 — one premium selection's outcome (gs-bet-core SelectionResult): WIN, LOSE, VOID, HALF_WIN, HALF_LOSE or
 * DEAD_HEAT_k; a run's result also names the selection.
 */
export interface Outcome {
  selectionId: number;
  outcome: string;
  name?: string | null;
}

export interface MarketResult {
  kind: 'WINNER' | 'VALUE' | 'VOID' | 'ROLLBACK' | 'OUTCOMES';
  winnerId?: number | null;
  winnerName?: string | null;
  value?: number | null;
  /** GS-894: a premium market's result — every selection's outcome. */
  outcomes?: Outcome[] | null;
  voidReason?: string | null;
  declaredBy?: string | null;
  /** GS-925: who approved it (a result past the approval window), when it was declared, and the declarer's reason. */
  approvedBy?: string | null;
  declaredAt?: string | null;
  reason?: string | null;
}

export interface ActiveRun {
  action: string;
  status: string;
  requestedBy?: string | null;
}

/** One market (Match Odds, a bookmaker market) or one fancy LINE. */
export interface ResultMarket {
  engine: Engine;
  marketId: string;
  name: string;
  selections: Selection[];
  status: string;
  answered: boolean;
  version: number;
  result: MarketResult | null;
  activeRun: ActiveRun | null;
  activeRunId: number | null;
  players?: number;
  staked?: string;
  /** GS-902: a fancy line's subtype (5 lottery, 6 odd/even read their number differently). */
  subtype?: number | null;
  /** GS-902 (FN-D8): a fancy line's / premium market's last bet stop — the outcome time a SETTLE takes by default. */
  lastStop?: string | null;
  /** GS-894 (premium): the line ("2.25"), how many selections may win (k), whether it may end in halves, the provider. */
  lineSpec?: string | null;
  winners?: number | null;
  halvesAllowed?: boolean;
  provider?: string | null;
}

export interface ResultEvent {
  key: string;
  engine: Engine;
  eventId: number;
  name: string;
  startTime: string | null;
  /** v2: how many markets still need a result / are answered / have a run in progress; legacy: 1 / 0 / 0. */
  unanswered: number;
  answered: number;
  inProgress: number;
  /** Legacy Match Odds / bookmaker: the event's one market comes with the list. */
  legacyMarket?: ResultMarket;
}

export interface Bet {
  betId: number;
  username: string;
  selectionName: string | null;
  side: string;
  stake: string;
  odds: string;
  runFrom: number | null;
  placedAt: string;
  voidReason: string | null;
}

/** A refusal with the server's own words (gs-settlement-service: {status, code, message}). */
export class ResultError extends Error {
  constructor(message: string, public readonly code: string, public readonly status: number) {
    super(message);
  }
}

export const VOID_REASONS = ['NO_RESULT', 'ABANDONED', 'TIE', 'CANCELLED', 'OTHER'];
const LEGACY_NO_RESULT = -99999;
const V2 = '/api/director/v2/settlement';

function errorOf(e: unknown): ResultError {
  if (axios.isAxiosError(e)) {
    const status = e.response ? e.response.status : 0;
    const data = (e.response && e.response.data) || {};
    const message = data.message || data.error || (status === 401 ? 'Your session ended: sign in again.'
      : status === 403 ? 'You are not allowed to do this.' : status ? 'The server refused (' + status + ').' : 'No answer from the server.');
    return new ResultError(String(message), String(data.code || status || 'NETWORK'), status);
  }
  return new ResultError(String(e), 'ERROR', 0);
}

/** A GET / POST to SES whose refusal comes back as a {@link ResultError} with the server's words (GS-904 shares them). */
export async function get<T>(url: string): Promise<T> {
  try {
    return (await axios.get<T>(url)).data;
  } catch (e) {
    throw errorOf(e);
  }
}

export async function post<T>(url: string, body?: unknown): Promise<T> {
  try {
    return (await axios.post<T>(url, body)).data;
  } catch (e) {
    throw errorOf(e);
  }
}

export function qs(params: Record<string, string | number | undefined>): string {
  return Object.keys(params).filter((k) => params[k] !== undefined && params[k] !== '')
    .map((k) => encodeURIComponent(k) + '=' + encodeURIComponent(String(params[k]))).join('&');
}

/** A random request id: a repeated SUBMIT answers with the first run instead of a second one. */
export function idempotencyKey(): string {
  const c = (window as unknown as { crypto?: Crypto }).crypto;
  if (c && typeof (c as Crypto & { randomUUID?: () => string }).randomUUID === 'function') {
    return (c as Crypto & { randomUUID: () => string }).randomUUID();
  }
  return 'k' + Date.now().toString(36) + Math.random().toString(36).slice(2, 12);
}

// ------------------------------------------------------------------------------------------------ legacy

interface LegacyList {
  matchList?: Array<{ eventId: number; name: string; sTime?: string; startDate?: string; priviousResultSelectionId?: number }>;
  resultOptions?: Record<string, Array<{ selectionId: number; runnerName: string }>>;
}

function legacyMarket(product: Product, eventId: number, options: Array<{ selectionId: number; runnerName: string }>,
                     previous?: number): ResultMarket {
  const selections = (options || []).map((o) => ({ id: o.selectionId, name: o.runnerName }));
  const winner = previous ? selections.find((s) => s.id === previous) : undefined;
  return {
    engine: 'legacy',
    marketId: String(eventId) + '-' + product,
    name: product === 1 ? 'Match Odds' : 'Bookmaker',
    selections,
    status: previous ? 'SETTLED' : 'OPEN',
    answered: !!previous,
    version: 0,
    result: previous ? { kind: 'WINNER', winnerId: previous, winnerName: winner ? winner.name : String(previous) } : null,
    activeRun: null,
    activeRunId: null,
  };
}

async function legacyEvents(product: Product, sportId: number, view: View, from: string, to: string): Promise<ResultEvent[]> {
  const url = view === 'pending'
    ? sesUrl('/api/director/pendingResults?' + qs({ sportId, marketType: product }))
    : sesUrl('/api/director/marketPreviousResult?' + qs({ sportId, marketType: product, sDateTime: from + ' 00:00:00',
      eDateTime: to + ' 23:59:59' }));
  const list = await get<LegacyList>(url);
  return (list.matchList || []).map((m) => {
    const options = (list.resultOptions || {})[String(m.eventId)] || [];
    const event: ResultEvent = {
      key: 'legacy-' + m.eventId,
      engine: 'legacy',
      eventId: m.eventId,
      name: m.name,
      startTime: m.sTime || m.startDate || null,
      unanswered: view === 'pending' ? 1 : 0,
      answered: view === 'answered' ? 1 : 0,
      inProgress: 0,
    };
    if (product !== 3) {
      event.legacyMarket = legacyMarket(product, m.eventId, options, m.priviousResultSelectionId);
    }
    return event;
  });
}

/** A legacy fancy event's lines (answered ones when {@code answered}). */
async function legacyLines(sportId: number, eventId: number, answered: boolean): Promise<ResultMarket[]> {
  const data = await get<{ fancyQuestions?: Array<{ marketId: number | string; marketName: string; answer?: number | string }> }>(
    sesUrl('/api/director/fancyQuestions?' + qs({ sportId, marketType: 3, eventId, isAnswerdQuestion: String(answered) })));
  return (data.fancyQuestions || []).map((q) => {
    const value = q.answer === undefined || q.answer === null || q.answer === '' ? null : Number(q.answer);
    return {
      engine: 'legacy' as Engine,
      marketId: String(q.marketId),
      name: q.marketName,
      selections: [],
      status: value === null ? 'OPEN' : 'SETTLED',
      answered: value !== null,
      version: 0,
      result: value === null ? null : value === LEGACY_NO_RESULT ? { kind: 'VOID', voidReason: 'NO_RESULT' } : { kind: 'VALUE', value },
      activeRun: null,
      activeRunId: null,
    };
  });
}

// ------------------------------------------------------------------------------------------------ engine v2

interface V2Event { eventId: number; eventName: string; startTime: string; unanswered: number; answered: number; inProgress: number }

interface V2Market {
  marketId: string;
  marketName: string;
  status: string;
  answered: boolean;
  resultVersion: number;
  activeRunId: number | null;
  activeRun?: ActiveRun;
  players: number;
  staked: string;
  subtype?: number;
  lastStop?: string;
  lineSpec?: string;
  winners?: number;
  halvesAllowed?: boolean;
  provider?: string;
  selections?: Array<{ selectionId: number; name: string }>;
  result: null | { kind: MarketResult['kind']; winnerSelectionId?: number; winnerName?: string; resultValue?: number;
    outcomes?: Outcome[]; voidReason?: string; declaredBy?: string; approvedBy?: string; declaredAt?: string; reason?: string };
}

async function v2Events(product: Product, sportId: number, view: View, from: string, to: string): Promise<ResultEvent[]> {
  const path = view === 'pending' ? '/results/pending?' + qs({ product, sportId })
    : '/results/answered?' + qs({ product, sportId, from, to });
  const data = await get<{ events?: V2Event[] }>(sesUrl(V2 + path));
  return (data.events || []).map((e) => ({
    key: 'v2-' + e.eventId,
    engine: 'v2' as Engine,
    eventId: e.eventId,
    name: e.eventName,
    startTime: e.startTime,
    unanswered: e.unanswered,
    answered: e.answered,
    inProgress: e.inProgress,
  }));
}

async function v2Markets(product: Product, eventId: number): Promise<ResultMarket[]> {
  const data = await get<{ markets?: V2Market[] }>(sesUrl(V2 + '/results/events/' + eventId + '/markets?' + qs({ product })));
  return (data.markets || []).map((m) => ({
    engine: 'v2' as Engine,
    marketId: m.marketId,
    name: m.marketName,
    selections: (m.selections || []).map((s) => ({ id: s.selectionId, name: s.name })),
    status: m.status,
    answered: m.answered,
    version: m.resultVersion,
    activeRun: m.activeRun || null,
    activeRunId: m.activeRunId,
    players: m.players,
    staked: m.staked,
    subtype: m.subtype === undefined ? null : m.subtype,
    lastStop: m.lastStop || null,
    lineSpec: m.lineSpec || null,
    winners: m.winners === undefined ? null : m.winners,
    halvesAllowed: !!m.halvesAllowed,
    provider: m.provider || null,
    result: m.result ? {
      kind: m.result.kind,
      winnerId: m.result.winnerSelectionId,
      winnerName: m.result.winnerName,
      value: m.result.resultValue,
      outcomes: m.result.outcomes || null,
      voidReason: m.result.voidReason,
      declaredBy: m.result.declaredBy,
      approvedBy: m.result.approvedBy || null,
      declaredAt: m.result.declaredAt || null,
      reason: m.result.reason || null,
    } : null,
  }));
}

// ------------------------------------------------------------------------------------------------ the console's calls

/**
 * A sport's events of a product, from both engines, earliest first (pending) or latest first (answered). An engine that
 * fails does not hide the other's: its error comes back beside the list. Premium exists on engine v2 only (no classic
 * premium market was ever resulted: SPORTSBOOK-ENGINE.md §2).
 */
export async function loadEvents(product: Product, sportId: number, view: View, from: string, to: string)
  : Promise<{ events: ResultEvent[]; errors: string[] }> {
  const sources: Array<[string, Promise<ResultEvent[]>]> = [['Engine v2 results: ', v2Events(product, sportId, view, from, to)]];
  if (product !== 4) {
    sources.unshift(['Classic results: ', legacyEvents(product, sportId, view, from, to)]);
  }
  const settled = await Promise.allSettled(sources.map((s) => s[1]));
  const events: ResultEvent[] = [];
  const errors: string[] = [];
  settled.forEach((r, i) => {
    if (r.status === 'fulfilled') {
      events.push(...r.value);
    } else {
      errors.push(sources[i][0] + (r.reason as Error).message);
    }
  });
  const time = (e: ResultEvent) => (e.startTime ? Date.parse(e.startTime.replace(' ', 'T')) || 0 : 0);
  events.sort((a, b) => (view === 'pending' ? time(a) - time(b) : time(b) - time(a)) || a.name.localeCompare(b.name));
  return { events, errors };
}

/** An event's markets (Match Odds / bookmaker) or lines (fancy) to result. */
export async function loadMarkets(product: Product, sportId: number, event: ResultEvent, view: View): Promise<ResultMarket[]> {
  if (event.engine === 'v2') {
    return v2Markets(product, event.eventId);
  }
  if (product === 3) {
    return legacyLines(sportId, event.eventId, view === 'answered');
  }
  return event.legacyMarket ? [event.legacyMarket] : [];
}

export async function loadBets(marketId: string): Promise<Bet[]> {
  const data = await get<{ bets?: Bet[] }>(sesUrl(V2 + '/markets/' + encodeURIComponent(marketId) + '/bets?limit=500'));
  return data.bets || [];
}

export interface Declaration {
  /** SETTLE / RESETTLE on a winner or a number; VOID (No result); UNSETTLE (undo); VOID_BETS; GS-926 CLOSE (stop bets). */
  action: 'settle' | 'resettle' | 'void' | 'unsettle' | 'bets/void' | 'close';
  winnerId?: number;
  value?: number;
  /** GS-894: a premium SETTLE / RESETTLE names every selection's outcome. */
  results?: Outcome[];
  voidReason?: string;
  reopen?: boolean;
  betIds?: number[];
  force?: boolean;
  /** GS-902 (FN-D8): bets placed after this instant are void; empty = the server's default (the bet stop). */
  outcomeKnownAt?: string;
  reason: string;
  key: string;
}

/** What the server answered: a v2 run (its id and state), or the legacy page's plain OK. */
export interface Declared {
  runId: number | null;
  status: string;
}

/** Declares one result. Legacy: the old page's calls (a winner, a number, or the number -99999 for No result). */
export async function declare(product: Product, sportId: number, event: ResultEvent, market: ResultMarket, d: Declaration)
  : Promise<Declared> {
  if (market.engine === 'legacy') {
    if (product === 3) {
      const finalRun = d.action === 'void' ? LEGACY_NO_RESULT : d.value;
      await post(sesUrl('/api/director/updateFancyMarketResult?' + qs({ sportId, marketType: 3, eventId: event.eventId,
        marketId: market.marketId, finalRun })));
    } else {
      await post(sesUrl('/api/director/updateMatchOddsBookMakerResult?' + qs({ sportId, marketType: product, eventId: event.eventId,
        selectionId: d.winnerId })));
    }
    return { runId: null, status: 'DONE' };
  }
  const body: Record<string, unknown> = { expectedVersion: market.version, reason: d.reason, idempotencyKey: d.key };
  if (d.winnerId !== undefined) {
    body.winnerSelectionId = d.winnerId;
  }
  if (d.value !== undefined) {
    body.resultValue = d.value;
  }
  if (d.results) {
    body.results = d.results.map((r) => ({ selectionId: r.selectionId, outcome: r.outcome }));
  }
  if (d.voidReason) {
    body.voidReason = d.voidReason;
  }
  if (d.reopen !== undefined) {
    body.reopen = d.reopen;
  }
  if (d.betIds) {
    body.betIds = d.betIds;
  }
  if (d.force) {
    body.force = true;
  }
  if (d.outcomeKnownAt) {
    body.outcomeKnownAt = d.outcomeKnownAt;
  }
  const answer = await post<{ run?: { runId: number; status: string } }>(
    sesUrl(V2 + '/markets/' + encodeURIComponent(market.marketId) + '/' + d.action), body);
  return { runId: answer.run ? answer.run.runId : null, status: answer.run ? answer.run.status : 'DONE' };
}

/** A v2 run's state now (RUNNING / DONE / AWAITING_APPROVAL / BLOCKED / CANCELLED). */
export async function runStatus(runId: number): Promise<string> {
  const data = await get<{ run?: { status: string } }>(sesUrl(V2 + '/runs/' + runId));
  return data.run ? data.run.status : 'UNKNOWN';
}

/** Codes after which a SETTLE may be sent again with `force` and a reason (D1: the feed still shows the market live). */
export const FORCEABLE = ['MARKET_STILL_LIVE', 'MARKET_STATE_UNKNOWN'];

// ------------------------------------------------------------------------------------------------ GS-903 the queues

/** A run of engine v2 (gs-settlement-service RunQueries): what it does, to which market, who asked, how far it got. */
export interface Run {
  runId: number;
  marketId: string;
  marketType: number | null;
  eventId: number | null;
  eventName: string | null;
  marketName: string | null;
  sportId: number | null;
  fancyKind: string | null;
  action: string;
  status: string;
  chunksTotal: number;
  chunksDone: number;
  usersTotal: number;
  requestedBy: string | null;
  approvedBy: string | null;
  forced: boolean;
  reason: string | null;
  betIds: number[] | null;
  createdAt: string | null;
  finishedAt: string | null;
  lastError: string | null;
  result: null | {
    kind: string;
    winnerSelectionId: number | null;
    winnerName: string | null;
    resultValue: number | null;
    resultView: number | null;
    voidReason: string | null;
    outcomeKnownAt: string | null;
    outcomes?: Outcome[];
  };
}

/**
 * GS-925 — one row of the operator action log (gs-settlement-service `operator_action_log`): who did what on the result
 * API, in which result role, when, what they entered, and how it ended. Append-only; for RCA.
 */
export interface ActionEntry {
  id: number;
  at: string;
  actor: string;
  /** SUPERVISOR | OPERATOR | NONE (not a result user: the attempt is kept). */
  role: string;
  /** CLOSE SETTLE VOID RESETTLE UNSETTLE VOID_BETS APPROVE RETRY CANCEL DEBIT_RECHECK DEBIT_WRITE_OFF DEFICITS_CLEAR */
  action: string;
  marketId: string | null;
  runId: number | null;
  caseId: number | null;
  /** OK | REFUSED | ERROR */
  outcome: string;
  code: string | null;
  message: string | null;
  request: null | {
    winnerSelectionId?: number | null;
    resultValue?: number | null;
    results?: Array<{ selectionId: number; outcome: string }> | null;
    voidReason?: string | null;
    reason?: string | null;
    betIds?: number[] | null;
    force?: boolean | null;
    outcomeKnownAt?: string | null;
  };
  requestId: string | null;
}

/** GS-925 — the action log, newest first: a market's whole history, a run's, or one person's. */
export async function loadActions(filter: { marketId?: string; runId?: number; actor?: string; limit?: number }): Promise<ActionEntry[]> {
  const data = await get<{ actions?: ActionEntry[] }>(sesUrl(V2 + '/actions?' + qs({ marketId: filter.marketId, runId: filter.runId,
    actor: filter.actor, limit: filter.limit || 100 })));
  return data.actions || [];
}

export interface DebitCase {
  caseId: number;
  userId: number;
  username: string | null;
  runId: number;
  marketId: string;
  eventName: string | null;
  state: string;
  shortfallInitial: string;
  shortfallNow: string;
  betsAutoVoided: number;
  openedAt: string | null;
  resolvedAt: string | null;
  resolvedBy: string | null;
  note: string | null;
  account?: { balance: string; exposure: string; available: string; betLock: boolean; withdrawLock: boolean };
}

/** The runs to review: {@code AWAITING_APPROVAL} (the checker's queue), {@code BLOCKED}, the forced ones, or the latest. */
export async function loadRuns(filter: { status?: string; forced?: boolean; limit?: number }): Promise<Run[]> {
  const data = await get<{ runs?: Run[] }>(sesUrl(V2 + '/runs?' + qs({ status: filter.status, forced: filter.forced ? 'true' : undefined,
    limit: filter.limit || 100 })));
  return data.runs || [];
}

/** approve (a second supervisor), retry (a BLOCKED run, once its cause is fixed), cancel (with a reason). */
export async function runAction(runId: number, action: 'approve' | 'retry' | 'cancel', reason?: string): Promise<Run | null> {
  const answer = await post<{ run?: Run }>(sesUrl(V2 + '/runs/' + runId + '/' + action),
    action === 'cancel' ? { reason, idempotencyKey: idempotencyKey() } : {});
  return answer.run || null;
}

export async function loadDebitCases(state?: string): Promise<DebitCase[]> {
  const data = await get<{ cases?: DebitCase[] }>(sesUrl(V2 + '/debit-cases?' + qs({ state, limit: 100 })));
  return data.cases || [];
}

/** recheck (the player may have money now), write-off (a supervisor, with a reason). */
export async function debitAction(caseId: number, action: 'recheck' | 'write-off', reason?: string): Promise<DebitCase | null> {
  const answer = await post<{ case?: DebitCase }>(sesUrl(V2 + '/debit-cases/' + caseId + '/' + action),
    action === 'write-off' ? { reason, idempotencyKey: idempotencyKey() } : {});
  return answer.case || null;
}
